import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { theme } from '../../theme/colors';
import { useUser } from '../../context/UserContext';
import AuthRequiredModal from '../AlertComponents/AuthRequiredModal';
import { updateBillVote, getBillVote, buildBillSlug } from '../../utils/utils';
import { buildStateBillPath, getStateName } from '../../utils/stateLegislationApi';
import { shape } from '../../theme/shape';

const STATUS_LABELS = {
  INTRODUCED: 'Introduced',
  IN_COMMITTEE: 'In Committee',
  REPORTED: 'Reported',
  ON_FLOOR: 'On Floor',
  PASSED_HOUSE: 'Passed House',
  PASSED_SENATE: 'Passed Senate',
  TO_PRESIDENT: 'To President',
  SIGNED: 'Signed',
  BECAME_LAW: 'Became Law',
  VETOED: 'Vetoed',
  FAILED: 'Failed',
};

function getStatusLabel(code) {
  if (!code) return null;
  return STATUS_LABELS[code] || null;
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateString;
  }
}

function getCosponsorsText(cosponsors) {
  if (!Array.isArray(cosponsors) || cosponsors.length === 0) return 'None';
  if (cosponsors.length <= 3) {
    return cosponsors.map((c) => `${(c.first_name || '').trim()} ${(c.last_name || '').trim()}`.trim()).join(', ');
  }
  const first = cosponsors.slice(0, 3).map((c) => `${(c.first_name || '').trim()} ${(c.last_name || '').trim()}`.trim()).join(', ');
  return `${first} +${cosponsors.length - 3} more`;
}

function deriveChamber(bill) {
  const t = (bill.type || '').toUpperCase();
  if (t.startsWith('S')) return 'Senate';
  if (t.startsWith('H')) return 'House';
  if (t.startsWith('A')) return 'Assembly';
  if (t === 'NE' || t === 'LB') return 'Legislature';
  return bill.origin_chamber || 'N/A';
}

function stripHtml(html) {
  return (html || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}

/** Plain-text preview length before ellipsis + read more (federal + state summaries) */
const SUMMARY_PREVIEW_CHARS = 200;

function voteFromBill(bill) {
  if (bill?.viewer_has_liked) return 'like';
  if (bill?.viewer_has_disliked) return 'dislike';
  return null;
}

export default function BillCard({ bill, onTakeAction, userState, verifiedVotesOnly = false }) {
  const navigate = useNavigate();
  const { dnaUserId } = useUser() || {};
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [vote, setVote] = useState(() => voteFromBill(bill));
  const [likeCount, setLikeCount] = useState(bill?.like_count ?? 0);
  const [dislikeCount, setDislikeCount] = useState(bill?.dislike_count ?? 0);
  const isStateBill = !!bill?.isStateBill;
  const stateCode = bill?.state || bill?.stateCode;
  const canTakeAction = !isStateBill || stateCode === userState;

  const billId = bill?.id;
  const billLikeCount = bill?.like_count ?? 0;
  const billDislikeCount = bill?.dislike_count ?? 0;
  const viewerHasLiked = !!bill?.viewer_has_liked;
  const viewerHasDisliked = !!bill?.viewer_has_disliked;

  // Counts and the viewer's own vote come from the list payload, which already
  // reflects the verified-votes filter. Avoids one request per card.
  useEffect(() => {
    setVote(viewerHasLiked ? 'like' : viewerHasDisliked ? 'dislike' : null);
    setLikeCount(billLikeCount);
    setDislikeCount(billDislikeCount);
  }, [billId, billLikeCount, billDislikeCount, viewerHasLiked, viewerHasDisliked]);

  const refreshVoteData = async () => {
    if (!bill?.id || !dnaUserId || isStateBill) return;
    try {
      const data = await getBillVote(bill.id, { verifiedOnly: verifiedVotesOnly });
      if (data.vote === true) setVote('like');
      else if (data.vote === false) setVote('dislike');
      else setVote(null);
      setLikeCount(data.like_count ?? 0);
      setDislikeCount(data.dislike_count ?? 0);
    } catch {
      // keep current state
    }
  };

  if (!bill) return null;

  const sessionText = isStateBill
    ? `${bill.session} Session • ${getStateName(stateCode)}`
    : `${bill.congress || 'N/A'}th Congress`;
  const chamberText = isStateBill ? deriveChamber(bill) : (bill.origin_chamber || 'N/A');
  const statusLabel = isStateBill
    ? (bill.status || null)
    : (getStatusLabel(bill.status_code) || bill.status || null);
  const cardLink = isStateBill
    ? buildStateBillPath(bill)
    : `/bills/${buildBillSlug(bill)}`;

  const summaryPlain = stripHtml(bill.summary);
  const showSummaryPreview = summaryPlain.length > 0;
  const summaryIsLong = summaryPlain.length > SUMMARY_PREVIEW_CHARS;
  const summaryPreviewText = summaryIsLong
    ? `${summaryPlain.slice(0, SUMMARY_PREVIEW_CHARS).trimEnd()}…`
    : summaryPlain;

  const handleTakeActionClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onTakeAction && onTakeAction(bill);
  };

  const handleViewDetails = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(cardLink, { state: isStateBill ? { bill } : undefined });
  };

  const handleLikeClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dnaUserId) { setAuthModalOpen(true); return; }
    try {
      if (vote === 'like') { await updateBillVote(bill.id, null); setVote(null); }
      else { await updateBillVote(bill.id, true); setVote('like'); }
      await refreshVoteData();
    } catch { /* keep UI state */ }
  };

  const handleDislikeClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dnaUserId) { setAuthModalOpen(true); return; }
    try {
      if (vote === 'dislike') { await updateBillVote(bill.id, null); setVote(null); }
      else { await updateBillVote(bill.id, false); setVote('dislike'); }
      await refreshVoteData();
    } catch { /* keep UI state */ }
  };

  return (
    <>
    <Box
      className="bills-card"
      component={Link}
      to={cardLink}
      state={isStateBill ? { bill } : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
        textDecoration: 'none',
        color: 'inherit',
        backgroundColor: '#ffffff',
        borderRadius: shape.radius.card,
        padding: '14px',
        border: '1px solid #cbd5e1',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          borderColor: 'rgba(4, 120, 87, 0.25)',
        },
      }}
    >
      {/* State badge — alignSelf prevents flex stretch to full card width */}
      {isStateBill && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            gap: 0.5,
            px: 1.5,
            py: 0.5,
            mb: 1,
            borderRadius: '12px',
            bgcolor: theme.primary,
            flexShrink: 0,
            maxWidth: '100%',
          }}
        >
          <AccountBalanceRoundedIcon sx={{ fontSize: 14, color: '#fff', flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: '#fff',
              lineHeight: 1.3,
              overflowWrap: 'anywhere',
            }}
          >
            {getStateName(stateCode)}
          </Typography>
        </Box>
      )}

      {/* Bill number row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <Typography
          component="span"
          sx={{ fontSize: '18px', fontWeight: 700, color: theme.primary }}
        >
          {bill.type ? `${String(bill.type).toUpperCase()} ` : ''}{bill.bill_number || 'N/A'}
        </Typography>

        {/* Like/Dislike — federal only */}
        {!isStateBill && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box
              component="button" type="button" onClick={handleLikeClick}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: '4px', px: 1.5, py: 0.5,
                borderRadius: '20px', border: 'none', cursor: 'pointer', backgroundColor: '#fff',
                boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.08)',
                transition: 'background-color 0.2s, box-shadow 0.2s',
                '&:hover': { backgroundColor: '#f8fafc', boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.12)' },
              }}
            >
              {vote === 'like' ? <ThumbUpIcon sx={{ fontSize: 18, color: theme.primary }} /> : <ThumbUpOutlinedIcon sx={{ fontSize: 18, color: '#64748b' }} />}
              <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: vote === 'like' ? theme.primary : '#64748b' }}>{likeCount}</Typography>
            </Box>
            <Box
              component="button" type="button" onClick={handleDislikeClick}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: '4px', px: 1.5, py: 0.5,
                borderRadius: '20px', border: 'none', cursor: 'pointer', backgroundColor: '#fff',
                boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.08)',
                transition: 'background-color 0.2s, box-shadow 0.2s',
                '&:hover': { backgroundColor: '#f8fafc', boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.12)' },
              }}
            >
              {vote === 'dislike' ? <ThumbDownIcon sx={{ fontSize: 18, color: theme.primary }} /> : <ThumbDownOutlinedIcon sx={{ fontSize: 18, color: '#64748b' }} />}
              <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: vote === 'dislike' ? theme.primary : '#64748b' }}>{dislikeCount}</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Meta row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px',
          flexWrap: 'wrap',
          rowGap: '6px',
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            color: '#334155',
            fontWeight: 500,
            minWidth: 0,
            flex: '1 1 auto',
            overflowWrap: 'anywhere',
          }}
        >
          {sessionText}
        </Typography>
        <Box
          sx={{
            backgroundColor: '#f8fafc',
            px: 1,
            py: 0.5,
            borderRadius: '6px',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#0f172a', textTransform: 'uppercase' }}>
            {chamberText}
          </Typography>
        </Box>
      </Box>

      {/* Title */}
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 600,
          color: '#0f172a',
          lineHeight: 1.4,
          marginBottom: '12px',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          overflowWrap: 'anywhere',
        }}
      >
        {bill.title || 'No title available'}
      </Typography>

      {/* Summary preview (nested <a> invalid — use span + navigate) */}
      {showSummaryPreview && (
        <Box sx={{ marginBottom: '14px' }}>
          <Typography
            component="div"
            sx={{ fontSize: 13, color: '#334155', lineHeight: 1.5 }}
          >
            {summaryPreviewText}
            {summaryIsLong && (
              <>
                {' '}
                <Box
                  component="span"
                  role="link"
                  tabIndex={0}
                  aria-label="Read full bill summary and details"
                  onClick={handleViewDetails}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleViewDetails(e);
                    }
                  }}
                  sx={{
                    color: theme.primary,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: 2,
                    '&:hover': { color: theme.primaryHover },
                  }}
                >
                  read more
                </Box>
              </>
            )}
          </Typography>
        </Box>
      )}

      {/* Details */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', minWidth: 0 }}>
          <PersonIcon sx={{ fontSize: 16, color: '#334155', mt: 0.2, flexShrink: 0 }} />
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 13,
              color: '#0f172a',
              lineHeight: 1.4,
              overflowWrap: 'anywhere',
            }}
          >
            Sponsor: {bill.sponsor_name || bill.sponsor || 'N/A'}
          </Typography>
        </Box>
        {bill.cosponsors && bill.cosponsors.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <GroupIcon sx={{ fontSize: 16, color: '#334155', mt: 0.2 }} />
            <Typography sx={{ flex: 1, fontSize: 13, color: '#0f172a', lineHeight: 1.4 }}>
              Cosponsors: {getCosponsorsText(bill.cosponsors)}
            </Typography>
          </Box>
        )}
        {statusLabel && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', minWidth: 0 }}>
            <AssignmentTurnedInIcon sx={{ fontSize: 16, color: '#334155', mt: 0.2, flexShrink: 0 }} />
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                color: '#0f172a',
                lineHeight: 1.4,
                overflowWrap: 'anywhere',
              }}
            >
              Status: {statusLabel}
            </Typography>
          </Box>
        )}
        {!isStateBill && bill.last_action_date && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <CalendarTodayIcon sx={{ fontSize: 16, color: '#334155', mt: 0.2 }} />
            <Typography sx={{ flex: 1, fontSize: 13, color: '#0f172a', lineHeight: 1.4 }}>
              {formatDate(bill.last_action_date)}: {bill.last_action_text || 'No action text'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Actions — wrapper pt separates metadata from buttons; mt: auto pins to bottom in grid */}
      <Box sx={{ marginTop: 'auto', pt: 2, width: '100%', flexShrink: 0 }}>
        {canTakeAction ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              width: '100%',
            }}
          >
            <Button
              fullWidth
              onClick={handleTakeActionClick}
              sx={{
                minHeight: 36,
                borderRadius: shape.radius.control,
                py: 0.75,
                px: 1.5,
                background: theme.gradient,
                color: '#ffffff',
                fontSize: 13,
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { background: theme.gradient, opacity: 0.92 },
              }}
            >
              Take Action
            </Button>
            <Button
              fullWidth
              onClick={handleViewDetails}
              sx={{
                minHeight: 36,
                borderRadius: shape.radius.control,
                py: 0.75,
                px: 1.5,
                background: '#ffffff',
                color: theme.primary,
                fontSize: 13,
                fontWeight: 600,
                textTransform: 'none',
                border: `1.5px solid ${theme.primary}`,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#f0fdf4', borderColor: theme.primaryHover, color: theme.primaryHover },
              }}
            >
              View Details
            </Button>
          </Box>
        ) : (
          <Button
            fullWidth
            onClick={handleViewDetails}
            sx={{
              minHeight: 36,
              borderRadius: shape.radius.control,
              py: 0.75,
              px: 1.5,
              background: 'transparent',
              color: theme.primary,
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              border: `1.5px solid ${theme.primary}`,
              '&:hover': { bgcolor: '#f0fdf4' },
            }}
          >
            View Details
          </Button>
        )}
      </Box>
    </Box>
    <AuthRequiredModal
      open={authModalOpen}
      onClose={() => setAuthModalOpen(false)}
      onLogin={() => { setAuthModalOpen(false); navigate('/auth/signup'); }}
    />
    </>
  );
}
